import {
	createContext,
	useContext,
	useCallback,
	useEffect,
	useState,
	type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
	getPagePreloadConfig,
	pagePreloadRegistry,
	type PagePreloadKey,
} from './registry';

export interface PagePreloadStatus {
	/** Whether the feature has any items (true = show list, false = show empty state) */
	hasItems: boolean;
	/** Whether the status check is still loading */
	isLoading: boolean;
	/** Whether there was an error fetching the status */
	isError: boolean;
}

export interface PagePreloadContextValue {
	/** Get the status for a specific page */
	getStatus: (key: PagePreloadKey) => PagePreloadStatus;
}

const defaultStatus: PagePreloadStatus = {
	hasItems: false,
	isLoading: true,
	isError: false,
};

const PagePreloadContext = createContext<PagePreloadContextValue | null>(null);

export interface PagePreloadProviderProps {
	children: ReactNode;
	/** Fallback to show while current page's preload data is loading */
	fallback?: ReactNode;
}

/**
 * Provider that enables page status lookups from the React Query cache.
 *
 * Pages are prefetched lazily when navigation links become visible or are hovered.
 * This provider gives child components access to the cached status data via
 * the `usePagePreloadStatus` hook.
 *
 * When a fallback is provided, it will be shown on pages that have a preload
 * config while that page's data is loading.
 *
 * @example
 * ```tsx
 * // In App.jsx, wrap authenticated routes
 * <PagePreloadProvider fallback={<PortalLoadingState />}>
 *   <AuthenticatedRoutes />
 * </PagePreloadProvider>
 * ```
 */
export const PagePreloadProvider = ({
	children,
	fallback = null,
}: PagePreloadProviderProps) => {
	const queryClient = useQueryClient();
	const { pathname } = useLocation();
	// State to force re-render when query completes
	const [, forceUpdate] = useState(0);

	// Find the config that matches the current path
	const matchingConfig = pagePreloadRegistry.find(
		(config) => pathname === config.path,
	);

	// Immediately fetch the query when the path matches
	useEffect(() => {
		if (!matchingConfig) return;

		// Skip if already in cache
		const cached = queryClient.getQueryData(matchingConfig.queryKey);
		if (cached) return;

		queryClient
			.fetchQuery({
				queryKey: matchingConfig.queryKey,
				queryFn: matchingConfig.queryFn,
				staleTime: 5 * 60 * 1000, // 5 minutes
			})
			.finally(() => {
				// Force re-render so getStatus() reads the updated cache
				forceUpdate((n) => n + 1);
			});
	}, [matchingConfig, queryClient]);

	const getStatus = useCallback(
		(key: PagePreloadKey): PagePreloadStatus => {
			const config = getPagePreloadConfig(key);
			if (!config) return defaultStatus;

			// Read from React Query cache (populated by prefetch)
			const queryState = queryClient.getQueryState(config.queryKey);

			if (!queryState) {
				// Not in cache yet - will be loading when page renders
				return defaultStatus;
			}

			return {
				hasItems:
					(queryState.data as { hasItems: boolean } | undefined)?.hasItems ??
					false,
				isLoading: queryState.fetchStatus === 'fetching',
				isError: queryState.status === 'error',
			};
		},
		[queryClient],
	);

	const value: PagePreloadContextValue = {
		getStatus,
	};

	// Only show fallback if:
	// 1. A fallback component is provided
	// 2. Current path matches a preload config
	// 3. That page's query is still loading (not in cache or fetching)
	const currentStatus = matchingConfig ? getStatus(matchingConfig.key) : null;
	if (fallback && matchingConfig && currentStatus?.isLoading) {
		return <>{fallback}</>;
	}

	return (
		<PagePreloadContext.Provider value={value}>
			{children}
		</PagePreloadContext.Provider>
	);
};

/**
 * Hook to access the page preload context
 */
export const usePagePreload = (): PagePreloadContextValue => {
	const context = useContext(PagePreloadContext);

	if (!context) {
		throw new Error('usePagePreload must be used within a PagePreloadProvider');
	}

	return context;
};

export default PagePreloadProvider;
